import { clamp01, lerp, smoothstep } from '../core/math';
import type { DefenseKind } from '../recog/events';
import type { HitResult, IncomingAttack, Openness } from './types';

/**
 * THE PUNCH-OUT EXCHANGE LOOP
 * ===========================
 *
 *      opponent TELEGRAPHS ──► player READS ──► player DODGES
 *              ▲                                     │
 *              │                          ┌──────────┴──────────┐
 *              │                     correct                  wrong / late
 *              │                          │                      │
 *              │                    PUNISH WINDOW           player is hit
 *              │                (guard visibly drops)            │
 *              │                          │                      │
 *              └───── window closes ◄── COMBO ◄──────────────────┘
 *
 * Why this shape, for a phone:
 *
 *  - It deletes every mechanic that depended on depth or floor space. What is
 *    left — head displacement and arm extension, both in torso space — is
 *    precisely what a single camera measures well.
 *  - It converts the game from "manage range" (which we measure badly) into
 *    "react correctly, then capitalise" (which we measure well, and which is
 *    what the player actually wants to train).
 *  - It gives offence a *reason to be timed*. In the free-spar build you could
 *    throw whenever; here, punching outside the window is punished, so the
 *    combination you throw after a successful dodge is the whole point.
 *
 * The single most important design choice in this file: **the punish window is
 * expressed as the opponent's guard dropping**, not as an invisible flag. The
 * renderer already draws glove height from the AI's guard values, so the window
 * is legible in the fiction — you see the hands drop and you go. No HUD element
 * is required for the player to understand it, though we show one anyway.
 */

export interface DodgeGrade {
  /** 0..1 — how well-timed and how correct the evasion was. */
  quality: number;
  /** ms between the attack becoming visible and the player starting to move. */
  reactionMs: number;
  /** ms between the player's peak evasion and the punch's impact instant. */
  timingErrorMs: number;
  perfect: boolean;
  kind: DefenseKind | 'block';
}

export interface ExchangeTuning {
  /** Punish window at base difficulty, ms. */
  windowMs: number;
  /** Extra window for a perfectly-timed dodge, ms. */
  perfectBonusMs: number;
  /** |peak - impact| under this counts as perfect. */
  perfectToleranceMs: number;
  /** Guard value while set. High enough that untimed punches get blocked. */
  guardSet: number;
  /** Guard value at full openness. */
  guardOpen: number;
  /** Each landed punch in a window shortens what remains, so combos must be brisk. */
  drainPerHitMs: number;
  /** Punching while they are set and pressing invites this counter, ms of delay. */
  counterDelayMs: number;
}

export const DEFAULT_EXCHANGE: ExchangeTuning = {
  windowMs: 800,
  perfectBonusMs: 450,
  perfectToleranceMs: 130,
  guardSet: 0.95,
  guardOpen: 0.08,
  drainPerHitMs: 90,
  counterDelayMs: 260,
};

export class ExchangeDirector {
  private openMs = 0;
  private totalMs = 0;
  private reason: Openness['reason'] = 'none';

  /** Rolling reaction-time samples, ms. The headline trainable statistic. */
  readonly reactions: number[] = [];
  /** Punches landed inside the current window. */
  private windowHits = 0;
  /** Best combo landed in a single window this round. */
  bestWindowCombo = 0;
  windowsOpened = 0;
  windowsUsed = 0;
  perfectDodges = 0;

  constructor(public tuning: ExchangeTuning = { ...DEFAULT_EXCHANGE }) {}

  reset(): void {
    this.openMs = 0;
    this.totalMs = 0;
    this.reason = 'none';
    this.reactions.length = 0;
    this.windowHits = 0;
    this.bestWindowCombo = 0;
    this.windowsOpened = 0;
    this.windowsUsed = 0;
    this.perfectDodges = 0;
  }

  get openness(): Openness {
    return {
      // Ease in fast, ease out over the last 200 ms so the guard visibly comes
      // back up rather than snapping — the player needs to see the window closing.
      value: this.totalMs <= 0 ? 0 : clamp01(smoothstep(0, 200, this.openMs)),
      remainingMs: Math.max(0, this.openMs),
      reason: this.reason,
    };
  }

  get isOpen(): boolean {
    return this.openMs > 0;
  }

  /** Guard value the opponent should be showing right now. */
  guardFor(baseGuard: number): number {
    const o = this.openness.value;
    return lerp(Math.min(baseGuard, this.tuning.guardSet), this.tuning.guardOpen, o);
  }

  update(dtMs: number): void {
    if (this.openMs > 0) {
      this.openMs -= dtMs;
      if (this.openMs <= 0) {
        this.openMs = 0;
        this.totalMs = 0;
        this.reason = 'none';
        if (this.windowHits > 0) this.windowsUsed++;
        this.bestWindowCombo = Math.max(this.bestWindowCombo, this.windowHits);
        this.windowHits = 0;
      }
    }
  }

  /**
   * Grade the player's defence against one incoming attack.
   *
   * `evasionStartMs` is when the player *began* moving, which is the honest
   * definition of reaction time — not when their head reached its furthest point.
   */
  grade(atk: IncomingAttack, result: HitResult, evasionStartMs: number, evasionPeakMs: number): DodgeGrade | null {
    const beaten = result.outcome === 'slipped' || result.outcome === 'miss' || result.outcome === 'blocked';
    if (!beaten) return null;

    const isBlock = result.defeatedBy === 'guard';

    // Only trust the evasion timestamps if they plausibly belong to THIS attack.
    // `lastEvasion*` persists indefinitely, so a movement made ten seconds ago
    // would otherwise be credited as a lightning reaction to this punch — which
    // is exactly what happened, and it poisoned the headline reaction statistic
    // with values lower than the player's configured reaction time.
    const belongsToThisAttack =
      evasionStartMs > 0 && evasionStartMs >= atk.tTelegraph - 80 && evasionStartMs <= atk.tImpact + 250;
    const reactionMs = belongsToThisAttack ? Math.max(0, evasionStartMs - atk.tTelegraph) : -1;
    const timingErrorMs = belongsToThisAttack && evasionPeakMs > 0 ? Math.abs(evasionPeakMs - atk.tImpact) : 1e6;
    const perfect = !isBlock && timingErrorMs <= this.tuning.perfectToleranceMs;

    // Timing is most of the grade. A dodge that happens to be in the right place
    // because the player was already leaning is worth far less than one that
    // peaks on the punch — otherwise "wander around constantly" beats "read it".
    const timing = 1 - smoothstep(this.tuning.perfectToleranceMs, 420, timingErrorMs);
    const quality = clamp01((isBlock ? 0.45 : 0.75) * timing + (isBlock ? 0.2 : 0.25));

    if (!isBlock && reactionMs >= 0) this.reactions.push(reactionMs);
    if (perfect) this.perfectDodges++;

    return {
      quality,
      reactionMs,
      timingErrorMs,
      perfect,
      kind: isBlock ? 'block' : ((result.defeatedBy as DefenseKind) ?? 'slip'),
    };
  }

  /**
   * Open the punish window. Length scales with how well the dodge was timed, so
   * a scrappy last-instant lean earns a sliver and a read punch earns a full
   * combination's worth. Blocking earns a window too, but a much smaller one —
   * evading should always be the better play.
   */
  openWindow(grade: DodgeGrade, difficultyScale = 1): void {
    const base = this.tuning.windowMs * difficultyScale;
    const ms = base * (0.35 + 0.65 * grade.quality) + (grade.perfect ? this.tuning.perfectBonusMs : 0);
    const scaled = grade.kind === 'block' ? ms * 0.45 : ms;
    // Never shorten an existing window — being punished for dodging twice would
    // be perverse.
    if (scaled > this.openMs) {
      this.openMs = scaled;
      this.totalMs = scaled;
      this.reason = grade.perfect ? 'perfect-dodge' : 'dodge';
      this.windowsOpened++;
      this.windowHits = 0;
    }
  }

  /** A short window granted for other reasons (heavy counter, exhaustion). */
  openIncidental(ms: number, reason: Openness['reason']): void {
    if (ms > this.openMs) {
      this.openMs = ms;
      this.totalMs = ms;
      this.reason = reason;
      this.windowsOpened++;
      this.windowHits = 0;
    }
  }

  /**
   * Register a landed player punch. Each one eats into the remaining window, so
   * a combination has a natural length rather than being unbounded — you cannot
   * stand there hitting a defenceless opponent forever.
   */
  registerLanded(): number {
    if (this.openMs <= 0) return 0;
    this.windowHits++;
    this.openMs = Math.max(0, this.openMs - this.tuning.drainPerHitMs);
    return this.windowHits;
  }

  /** Mean of the most recent reaction samples, ms. 0 when none yet. */
  meanReactionMs(n = 12): number {
    if (this.reactions.length === 0) return 0;
    const xs = this.reactions.slice(-n);
    return xs.reduce((a, b) => a + b, 0) / xs.length;
  }

  bestReactionMs(): number {
    return this.reactions.length ? Math.min(...this.reactions) : 0;
  }

  /** Fraction of opened windows the player actually cashed in. */
  get utilisation(): number {
    return this.windowsOpened > 0 ? this.windowsUsed / this.windowsOpened : 0;
  }

  get hitsThisWindow(): number {
    return this.windowHits;
  }
}
